import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBlocker } from '@tanstack/react-router';
import { Loader2, CheckCircle } from 'lucide-react';
import { UnsavedChangesModal } from './components/UnsavedChangesModal';
import { Image as ImageIcon, Play } from 'lucide-react';
import { SiMeta, SiTiktok, SiSnapchat } from 'react-icons/si';
import { FaLinkedin } from 'react-icons/fa6';
import type { IconType } from 'react-icons';
import { useSidebar } from '@/components/ui/sidebar';
import { type MockupType, ASSET_TYPE_TO_MOCKUP_TYPE, MOCKUP_OPTIONS } from './types';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import type { FieldIssue } from './types';
import { AssetPreviewHeader } from './components/Header';
import { DcoBadge, isDcoEnabled } from '@/components/DcoBadge';
import { PAGE_PADDING } from '@/components/layout/layoutConstants';
import { PreviewAndAdDetails } from './components/PreviewAndAdDetails';
import { SearchOrchestrator } from './components/search-details/SearchOrchestrator';
import { CampaignDetails } from './components/CampaignDetails';
import { DownloadAssetsModal } from './components/DownloadAssetsModal';
import { Activity } from './components/Activity';
import { fetchCampaigns, updateCampaign, type CampaignsResponse } from '@/api/campaigns';
import { usePermissions } from '@/hooks/usePermissions';
import { getAssetTypesForPlatform, getAssetTypesForPlatformAndSize } from '@/config/assetTypes';
import { isSearchCampaign, getSearchFormatType } from './components/search-details/search-config';

// ─── Format card visuals (copied from MockupsPage) ───────────────────────────

type FormatVisual =
  | 'single-square'
  | 'single-portrait'
  | 'carousel-square'
  | 'carousel-portrait'
  | 'collection-square'
  | 'document'
  | 'reels';

function FormatPreview({ visual }: { visual: FormatVisual }) {
  const card = 'bg-white rounded-md ring-1 ring-slate-300 flex items-center justify-center';
  if (visual === 'single-square')
    return (
      <div className={`w-14 h-14 ${card}`}>
        <ImageIcon className="w-5 h-5 text-slate-300" />
      </div>
    );
  if (visual === 'single-portrait')
    return (
      <div className={`w-9 h-14 ${card}`}>
        <ImageIcon className="w-4 h-4 text-slate-300" />
      </div>
    );
  if (visual === 'reels')
    return (
      <div className={`w-9 h-14 ${card}`}>
        <Play className="w-4 h-4 text-slate-300 fill-slate-300" />
      </div>
    );
  if (visual === 'document')
    return (
      <div className={`w-10 h-14 ${card} flex-col gap-1.5 px-2`}>
        <div className="w-full h-1 bg-slate-200 rounded-full" />
        <div className="w-full h-1 bg-slate-200 rounded-full" />
        <div className="w-3/4 h-1 bg-slate-200 rounded-full" />
      </div>
    );
  if (visual === 'carousel-square')
    return (
      <div className="relative w-16 h-14">
        <div className="absolute right-0 top-1.5 w-12 h-11 bg-white/60 rounded-md ring-1 ring-slate-300" />
        <div className={`absolute left-0 top-0 w-12 h-11 ${card} gap-1`}>
          <div className="w-1.5 h-1.5 rounded-full bg-slate-900" />
          <div className="w-1.5 h-1.5 rounded-full bg-slate-200" />
          <div className="w-1.5 h-1.5 rounded-full bg-slate-200" />
        </div>
      </div>
    );
  if (visual === 'carousel-portrait')
    return (
      <div className="relative w-12 h-14">
        <div className="absolute right-0 top-1.5 w-7 h-12 bg-white/60 rounded-md ring-1 ring-slate-300" />
        <div className={`absolute left-0 top-0 w-7 h-12 ${card} flex-col gap-1`}>
          <div className="w-1.5 h-1.5 rounded-full bg-slate-900" />
          <div className="w-1.5 h-1.5 rounded-full bg-slate-200" />
        </div>
      </div>
    );
  if (visual === 'collection-square')
    return (
      <div className="space-y-1 w-14">
        <div className="w-full h-8 bg-white rounded-md ring-1 ring-slate-300 flex items-center justify-center">
          <ImageIcon className="w-3 h-3 text-slate-300" />
        </div>
        <div className="flex gap-1">
          <div className="flex-1 h-5 bg-white rounded shadow-sm" />
          <div className="flex-1 h-5 bg-white rounded shadow-sm" />
        </div>
      </div>
    );
  return null;
}

// ─── Asset type → card metadata ───────────────────────────────────────────────

const FORMAT_CARD_META: Record<string, { label: string; ratio: string; visual: FormatVisual }> = {
  'meta-unified-social-ad': {
    label: 'Single Image or Video',
    ratio: '1:1 + 9:16',
    visual: 'single-square',
  },
  'meta-unified-carousel': { label: 'Carousel', ratio: '1:1 + 9:16', visual: 'carousel-square' },
  'meta-unified-dynamic-creative': {
    label: 'Dynamic Creative',
    ratio: '1:1 + 9:16',
    visual: 'single-square',
  },
  'meta-unified-collection': {
    label: 'Collection',
    ratio: '1:1 + 9:16',
    visual: 'collection-square',
  },
  'meta-flexible-ad': { label: 'Flexible Ad', ratio: 'Multiple', visual: 'single-square' },
  'linkedin-1x1-social-ad': {
    label: 'Single Image or Video',
    ratio: '1:1',
    visual: 'single-square',
  },
  'linkedin-1x1-carousel': { label: 'Carousel', ratio: '1:1', visual: 'carousel-square' },
  'linkedin-document': { label: 'Document', ratio: 'Doc', visual: 'document' },
  'tiktok-ad': { label: 'Video', ratio: '9:16', visual: 'reels' },
  'snapchat-9x16-single-image': {
    label: 'Single Image or Video',
    ratio: '9:16',
    visual: 'single-portrait',
  },
  'snapchat-9x16-story': { label: 'Story Ad', ratio: '9:16', visual: 'carousel-portrait' },
};

// ─── Platform icon + colour ───────────────────────────────────────────────────

interface PlatformMeta {
  Icon: IconType;
  color: string;
}

function getPlatformMeta(platform: string): PlatformMeta {
  const p = platform.toLowerCase();
  if (p.includes('meta') || p.includes('facebook') || p.includes('instagram'))
    return { Icon: SiMeta, color: '#0082FB' };
  if (p.includes('linkedin')) return { Icon: FaLinkedin, color: '#0A66C2' };
  if (p.includes('tiktok')) return { Icon: SiTiktok, color: '#111111' };
  if (p.includes('snapchat')) return { Icon: SiSnapchat, color: '#F7C600' };
  return { Icon: SiMeta, color: '#64748b' };
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function AssetPreviewPage() {
  const { setOpen } = useSidebar();
  const queryClient = useQueryClient();

  const params = useParams({ strict: false }) as { campaignId?: string };
  const campaignId = params.campaignId;

  const [showErrors, setShowErrors] = useState(false);
  const [showWarnings, setShowWarnings] = useState(false);
  const [localMockupType, setLocalMockupType] = useState<MockupType | null>(null);
  const [showFormatSelection, setShowFormatSelection] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isSavingAndLeaving, setIsSavingAndLeaving] = useState(false);
  const [fieldIssues, setFieldIssues] = useState<Record<string, FieldIssue>>({});

  // Drop the stored issues for fields the user has just edited.
  //
  // fieldIssues is a snapshot from the last validation run, but the counts and
  // previews around it are live. Left alone the two contradict each other — an
  // asset section reading "2 / 10" directly above a "less than 2 images"
  // warning generated when it held one. Only the touched keys are cleared, so
  // unrelated findings stay on screen and the user keeps their worklist.
  const clearFieldIssues = useCallback((keys: string[]) => {
    if (keys.length === 0) return;
    setFieldIssues((prev) => {
      const stale = keys.filter((k) => k in prev);
      if (stale.length === 0) return prev; // no-op keeps referential equality
      const next = { ...prev };
      for (const k of stale) delete next[k];
      return next;
    });
  }, []);
  const [hasValidated, setHasValidated] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [anyVariantHasErrors, setAnyVariantHasErrors] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveNote, setApproveNote] = useState('');
  const [isVersionLocked, setIsVersionLocked] = useState(false);
  // Refs that PreviewAndAdDetails keeps up-to-date with the current save/validate/approve handlers.
  const saveFnRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const validateFnRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const approveFnRef = useRef<((note: string) => Promise<void>) | undefined>(undefined);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [setOpen]);

  // ── Campaign fetch — read directly from the campaigns list query (same source as the table) ──
  const { data: campaignsData, isLoading } = useQuery({
    queryKey: ['campaigns', false],
    queryFn: () => fetchCampaigns(false),
    staleTime: 5 * 60 * 1000,
  });
  const campaign = campaignsData?.campaigns.find((c) => c.id === campaignId);

  // ── Permissions ──────────────────────────────────────────────────────────
  const {
    data: permissions,
    isLoading: isLoadingPermissions,
    isError: isPermissionsError,
  } = usePermissions();
  const canEditAssets =
    !isLoadingPermissions &&
    !isPermissionsError &&
    permissions?.asset_modal_permission === 'all_editable';

  // ── Search campaign detection ──────────────────────────────────────────────
  const isSearch = isSearchCampaign(campaign);
  const searchFormatType = isSearch ? getSearchFormatType(campaign) : undefined;

  // Block in-app navigation and browser close/refresh when there are unsaved changes
  const blocker = useBlocker({
    shouldBlockFn: () => isDirty,
    enableBeforeUnload: () => isDirty,
    withResolver: true,
  });

  const setAssetTypeMutation = useMutation({
    mutationFn: (assetTypeId: string) =>
      updateCampaign(campaignId!, { asset_type_id: assetTypeId }),
    onSuccess: () => {
      setShowFormatSelection(false);
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  // ── Available format types for this platform ──────────────────────────────
  // Computed at component level so the auto-select effect below can use it.
  // Use format + size to narrow options — when unambiguous, auto-selects below.
  const platform = campaign?.platform ?? '';
  const campaignFormat = campaign?.format || null;
  const campaignSize = campaign?.size || null;

  const platformTypes = platform
    ? getAssetTypesForPlatformAndSize(platform, campaignSize, campaignFormat)
    : [
        ...getAssetTypesForPlatform('Meta').filter(
          (t) => t.features.isUnifiedMeta || t.features.isFlexibleAd,
        ),
        ...getAssetTypesForPlatform('LinkedIn'),
        ...getAssetTypesForPlatform('TikTok'),
        ...getAssetTypesForPlatform('Snapchat'),
      ];
  const availableTypes = platformTypes.filter((t) => ASSET_TYPE_TO_MOCKUP_TYPE[t.id]);

  // ── Derive mockup type ────────────────────────────────────────────────────
  const derivedMockupType: MockupType | undefined = campaign?.asset_type_id
    ? ASSET_TYPE_TO_MOCKUP_TYPE[campaign.asset_type_id]
    : undefined;

  const mockupType = showFormatSelection
    ? null
    : (derivedMockupType ??
      localMockupType ??
      // When there's exactly one format, use it immediately so the selection
      // screen never flashes while the useEffect / mutation catches up.
      (availableTypes.length === 1 ? ASSET_TYPE_TO_MOCKUP_TYPE[availableTypes[0].id] : null));

  // Effective asset type ID — mirrors the mockupType fallback so uploads/saves always
  // have an asset_type_id even before the auto-select mutation has persisted it.
  const effectiveAssetTypeId =
    campaign?.asset_type_id ?? (availableTypes.length === 1 ? availableTypes[0].id : undefined);
  const formatLabel = (() => {
    if (!campaign?.asset_type_id) return undefined;
    const cardMeta = FORMAT_CARD_META[campaign.asset_type_id];
    if (!cardMeta) return undefined;
    const group = MOCKUP_OPTIONS.find((o) => o.value === derivedMockupType)?.group;
    return group ? `${group} – ${cardMeta.label}` : cardMeta.label;
  })();

  // ── Shared header props ───────────────────────────────────────────────────
  const campaignLabel = campaign?.campaign_id;

  const errorCount = Object.values(fieldIssues).reduce((sum, i) => sum + i.errors.length, 0);
  const warningCount = Object.values(fieldIssues).reduce((sum, i) => sum + i.warnings.length, 0);
  const hasErrors = errorCount > 0 || anyVariantHasErrors;

  // ── Auto-select when there's only one format option ───────────────────────
  // Skips the format selection card for single-format platforms (e.g. TikTok).
  // Uses local state only — asset_type_id is persisted when the user saves.
  useEffect(() => {
    if (
      availableTypes.length === 1 &&
      !derivedMockupType &&
      !localMockupType &&
      !showFormatSelection
    ) {
      const only = availableTypes[0];
      setLocalMockupType(ASSET_TYPE_TO_MOCKUP_TYPE[only.id]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableTypes.length, derivedMockupType, localMockupType]);

  // ── Loading (only shows on cold direct-URL navigation, not from CT table) ──
  if (campaignId && isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-slate-400">
          <div className="w-6 h-6 border-2 border-slate-200 border-t-[#0014cc] rounded-full animate-spin" />
          <span className="text-sm">Loading campaign…</span>
        </div>
      </div>
    );
  }

  // ── Search campaign preview ──────────────────────────────────────────────
  if (isSearch && searchFormatType) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <AssetPreviewHeader
          campaignId={campaignLabel}
          isDco={isDcoEnabled(campaign?.dco_status)}
          errorCount={errorCount}
          warningCount={warningCount}
          fieldIssues={fieldIssues}
          showErrors={showErrors || errorCount > 0}
          onShowErrorsChange={setShowErrors}
          showWarnings={showWarnings}
          onShowWarningsChange={setShowWarnings}
          onSave={() => saveFnRef.current?.()}
          onSaveAndValidate={() => validateFnRef.current?.()}
          isDirty={isDirty}
          isSaving={isSaving}
          isValidating={isValidating}
          hasValidated={hasValidated}
          hasErrors={hasErrors}
          isApproved={isApproved}
          isApproving={isApproving}
          onApprove={() => {
            setApproveNote('');
            setShowApproveModal(true);
          }}
          readOnly={!canEditAssets}
          isLocked={isVersionLocked}
          onDownload={
            permissions?.can_download_assets ? () => setShowDownloadModal(true) : undefined
          }
        />

        {showDownloadModal && campaignId && (
          <DownloadAssetsModal
            campaignId={campaignId}
            isSearch={true}
            nameplate={campaign?.nameplate}
            onClose={() => setShowDownloadModal(false)}
          />
        )}

        <div
          className={`flex-1 flex gap-4 ${PAGE_PADDING} min-h-0 relative`}
          aria-busy={isValidating || isSaving || isApproving}
          {...(isValidating || isSaving || isApproving
            ? ({ inert: '' } as React.HTMLAttributes<HTMLDivElement>)
            : {})}
        >
          {(isValidating || isSaving || isApproving) && (
            <div className="absolute inset-0 z-30 bg-white/60 backdrop-blur-[1px] flex items-center justify-center cursor-not-allowed rounded-lg">
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-slate-200">
                <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
                <span className="text-sm text-slate-600 font-medium">
                  {isValidating ? 'Validating…' : isSaving ? 'Saving…' : 'Approving…'}
                </span>
              </div>
            </div>
          )}
          <SearchOrchestrator
            formatType={searchFormatType}
            campaignId={campaignId}
            campaign={campaign}
            showErrors={showErrors || errorCount > 0}
            showWarnings={showWarnings}
            fieldIssues={fieldIssues}
            readOnly={!canEditAssets}
            saveFnRef={saveFnRef}
            validateFnRef={validateFnRef}
            approveFnRef={approveFnRef}
            onDirtyChange={setIsDirty}
            onSavingChange={setIsSaving}
            onValidatingChange={setIsValidating}
            onApprovingChange={setIsApproving}
            onHasValidatedChange={setHasValidated}
            onIsApprovedChange={setIsApproved}
            onAnyVariantHasErrorsChange={setAnyVariantHasErrors}
            onValidationResult={setFieldIssues}
            onLockedChange={setIsVersionLocked}
            onValidationComplete={(hasErrs, hasWarnings) => {
              if (hasErrs) setShowErrors(true);
              if (hasWarnings) setShowWarnings(true);
            }}
          />
          <div className="w-[28%] flex flex-col gap-4 min-h-0">
            <CampaignDetails
              campaign={campaign ?? null}
              fieldIssues={fieldIssues}
              showErrors={showErrors || errorCount > 0}
              showWarnings={showWarnings}
              editableFields={permissions?.editable_campaign_fields ?? []}
            />
            <Activity campaignId={campaignId} onApprovalDeleted={() => setIsApproved(false)} />
          </div>
        </div>

        {/* ── Approve confirmation modal (same as social) ───────────────────── */}
        {canEditAssets && showApproveModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={() => setShowApproveModal(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setShowApproveModal(false);
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="approve-modal-title-search"
              className="w-[480px] bg-white rounded-xl border border-slate-200 shadow-xl flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl flex-shrink-0">
                <p
                  id="approve-modal-title-search"
                  className="text-xs font-semibold uppercase tracking-wider text-slate-600"
                >
                  Approve Assets
                </p>
              </div>
              <div className="px-6 py-5">
                <p className="text-sm text-slate-600 mb-4">
                  Add a note to go with this approval. It will appear in the activity feed.
                </p>
                <textarea
                  autoFocus
                  value={approveNote}
                  onChange={(e) => setApproveNote(e.target.value)}
                  placeholder="e.g. Looks great — approved for Q2 launch."
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500/50 resize-none transition-all"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setShowApproveModal(false);
                  }}
                />
                <div className="flex gap-2 mt-4 justify-end">
                  <button
                    onClick={() => setShowApproveModal(false)}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={isApproving || !approveNote.trim()}
                    onClick={async () => {
                      try {
                        await approveFnRef.current?.(approveNote);
                        setShowApproveModal(false);
                      } catch {
                        // error toast already shown in handler
                      }
                    }}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 disabled:opacity-50 transition-colors"
                  >
                    {isApproving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    {isApproving ? 'Approving…' : 'Approve'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Unsaved changes confirmation modal ─────────────────────────────── */}
        {blocker.status === 'blocked' && (
          <UnsavedChangesModal
            onStay={() => blocker.reset()}
            onDiscard={() => blocker.proceed()}
            onSave={async () => {
              await saveFnRef.current?.();
              blocker.proceed();
            }}
            canSave={canEditAssets}
            isSaving={isSavingAndLeaving}
            setIsSaving={setIsSavingAndLeaving}
          />
        )}
      </div>
    );
  }

  // ── Format selection screen ───────────────────────────────────────────────
  if (!mockupType) {
    const platformMeta = platform ? getPlatformMeta(platform) : null;

    return (
      <div className="h-full flex flex-col overflow-hidden">
        <AssetPreviewHeader
          campaignId={campaignLabel}
          isDco={isDcoEnabled(campaign?.dco_status)}
          errorCount={0}
          warningCount={0}
          showErrors={false}
          onShowErrorsChange={setShowErrors}
          showWarnings={false}
          onShowWarningsChange={setShowWarnings}
          showActions={false}
        />

        <div className={`flex-1 flex items-center justify-center ${PAGE_PADDING} overflow-auto`}>
          <Card className="w-full max-w-xl border-slate-300 shadow-sm">
            <CardHeader className="items-center text-center pb-4">
              {platformMeta && (
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-1"
                  style={{ backgroundColor: `${platformMeta.color}18` }}
                >
                  <platformMeta.Icon className="w-6 h-6" style={{ color: platformMeta.color }} />
                </div>
              )}
              <h2 className="text-lg font-semibold text-slate-800">Select Ad Format</h2>
              {campaign && (
                <div className="flex items-center justify-center gap-2">
                  <p className="text-sm text-slate-400">{campaign.campaign_id}</p>
                  {isDcoEnabled(campaign.dco_status) && <DcoBadge />}
                </div>
              )}
            </CardHeader>

            <CardContent className="pb-6">
              {(() => {
                const renderFormatButton = (assetType: (typeof availableTypes)[number]) => {
                  const mt = ASSET_TYPE_TO_MOCKUP_TYPE[assetType.id];
                  const cardMeta = FORMAT_CARD_META[assetType.id];
                  return (
                    <button
                      key={assetType.id}
                      onClick={() => {
                        if (campaignId) {
                          setAssetTypeMutation.mutate(assetType.id);
                        } else {
                          setLocalMockupType(mt);
                          setShowFormatSelection(false);
                        }
                      }}
                      disabled={setAssetTypeMutation.isPending}
                      className="flex flex-col rounded-xl border-2 overflow-hidden border-slate-200 bg-slate-50 hover:border-[#0014cc] hover:shadow-md transition-all text-left disabled:opacity-50 w-36"
                    >
                      <div className="h-20 flex items-center justify-center bg-slate-50 p-3 w-full">
                        {cardMeta && <FormatPreview visual={cardMeta.visual} />}
                      </div>
                      <div className="flex-1 px-3 py-2.5 border-t border-slate-100 bg-white">
                        <p className="text-sm font-semibold text-slate-800 leading-tight">
                          {cardMeta?.label ?? assetType.title}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          {cardMeta?.ratio ?? assetType.description}
                        </p>
                      </div>
                    </button>
                  );
                };

                if (availableTypes.length === 5) {
                  return (
                    <div className="flex flex-col gap-3 items-center">
                      <div className="flex gap-3 justify-center">
                        {availableTypes.slice(0, 3).map(renderFormatButton)}
                      </div>
                      <div className="flex gap-3 justify-center">
                        {availableTypes.slice(3).map(renderFormatButton)}
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="flex flex-wrap gap-3 justify-center">
                    {availableTypes.map(renderFormatButton)}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── Main preview page ─────────────────────────────────────────────────────
  return (
    <div className="h-full flex flex-col overflow-hidden">
      <AssetPreviewHeader
        campaignId={campaignLabel}
        isDco={isDcoEnabled(campaign?.dco_status)}
        errorCount={errorCount}
        warningCount={warningCount}
        fieldIssues={fieldIssues}
        showErrors={showErrors || errorCount > 0}
        onShowErrorsChange={setShowErrors}
        showWarnings={showWarnings}
        onShowWarningsChange={setShowWarnings}
        onSave={() => saveFnRef.current?.()}
        onSaveAndValidate={() => validateFnRef.current?.()}
        isDirty={isDirty}
        isSaving={isSaving}
        isValidating={isValidating}
        hasValidated={hasValidated}
        hasErrors={hasErrors}
        isApproved={isApproved}
        isApproving={isApproving}
        onApprove={() => {
          setApproveNote('');
          setShowApproveModal(true);
        }}
        readOnly={!canEditAssets}
        onDownload={permissions?.can_download_assets ? () => setShowDownloadModal(true) : undefined}
      />

      {showDownloadModal && campaignId && (
        <DownloadAssetsModal
          campaignId={campaignId}
          isSearch={false}
          nameplate={campaign?.nameplate}
          onClose={() => setShowDownloadModal(false)}
        />
      )}

      {/* ── Unsaved changes confirmation modal ─────────────────────────────── */}
      {blocker.status === 'blocked' && (
        <UnsavedChangesModal
          onStay={() => blocker.reset()}
          onDiscard={() => blocker.proceed()}
          onSave={async () => {
            await saveFnRef.current?.();
            blocker.proceed();
          }}
          canSave={canEditAssets}
          isSaving={isSavingAndLeaving}
          setIsSaving={setIsSavingAndLeaving}
        />
      )}

      {/* ── Approve confirmation modal ──────────────────────────────────────── */}
      {canEditAssets && showApproveModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setShowApproveModal(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setShowApproveModal(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="approve-modal-title-social"
            className="w-[480px] bg-white rounded-xl border border-slate-200 shadow-xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl flex-shrink-0">
              <p
                id="approve-modal-title-social"
                className="text-xs font-semibold uppercase tracking-wider text-slate-600"
              >
                Approve Assets
              </p>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-slate-600 mb-4">
                Add a note to go with this approval. It will appear in the activity feed.
              </p>
              <textarea
                autoFocus
                value={approveNote}
                onChange={(e) => setApproveNote(e.target.value)}
                placeholder="e.g. Looks great — approved for Q2 launch."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500/50 resize-none transition-all"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setShowApproveModal(false);
                }}
              />
              <div className="flex gap-2 mt-4 justify-end">
                <button
                  onClick={() => setShowApproveModal(false)}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  disabled={isApproving || !approveNote.trim()}
                  onClick={async () => {
                    try {
                      await approveFnRef.current?.(approveNote);
                      setShowApproveModal(false);
                    } catch {
                      // error toast already shown in handler
                    }
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 disabled:opacity-50 transition-colors"
                >
                  {isApproving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  {isApproving ? 'Approving…' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className={`flex-1 flex gap-4 ${PAGE_PADDING} min-h-0 relative`}
        aria-busy={isValidating || isSaving || isApproving}
        {...(isValidating || isSaving || isApproving
          ? ({ inert: '' } as React.HTMLAttributes<HTMLDivElement>)
          : {})}
      >
        {(isValidating || isSaving || isApproving) && (
          <div className="absolute inset-0 z-30 bg-white/60 backdrop-blur-[1px] flex items-center justify-center cursor-not-allowed rounded-lg">
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-slate-200">
              <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
              <span className="text-sm text-slate-600 font-medium">
                {isValidating ? 'Validating…' : isSaving ? 'Saving…' : 'Approving…'}
              </span>
            </div>
          </div>
        )}
        <PreviewAndAdDetails
          mockupType={mockupType}
          showErrors={showErrors || errorCount > 0}
          showWarnings={showWarnings}
          fieldIssues={fieldIssues}
          formatLabel={formatLabel}
          onBack={() => setShowFormatSelection(true)}
          onDeleteAll={() => {
            // Clear local UI state immediately
            setLocalMockupType(null);
            setShowFormatSelection(true);
            // Reset all validation / approval state so stale results don't linger
            setFieldIssues({});
            setHasValidated(false);
            setIsApproved(false);
            setAnyVariantHasErrors(false);
            setShowErrors(false);
            setShowWarnings(false);
            // Patch the campaigns list cache so derivedMockupType is null right away
            for (const includeArchived of [false, true]) {
              queryClient.setQueryData<CampaignsResponse>(['campaigns', includeArchived], (prev) =>
                prev
                  ? {
                      ...prev,
                      campaigns: prev.campaigns.map((c) =>
                        c.id === campaignId ? { ...c, asset_type_id: null } : c,
                      ),
                    }
                  : prev,
              );
            }
          }}
          campaignId={campaignId}
          campaign={campaign}
          effectiveAssetTypeId={effectiveAssetTypeId}
          saveFnRef={saveFnRef}
          validateFnRef={validateFnRef}
          approveFnRef={approveFnRef}
          onDirtyChange={setIsDirty}
          onSavingChange={setIsSaving}
          onValidatingChange={setIsValidating}
          onApprovingChange={setIsApproving}
          onHasValidatedChange={setHasValidated}
          onIsApprovedChange={setIsApproved}
          onAnyVariantHasErrorsChange={setAnyVariantHasErrors}
          onValidationResult={setFieldIssues}
          onFieldsChanged={clearFieldIssues}
          onValidationComplete={(hasErrs, hasWarnings) => {
            if (hasErrs) setShowErrors(true);
            if (hasWarnings) setShowWarnings(true);
          }}
          readOnly={!canEditAssets}
        />
        <div className="w-[28%] flex flex-col gap-4 min-h-0">
          <CampaignDetails
            campaign={campaign ?? null}
            fieldIssues={fieldIssues}
            showErrors={showErrors || errorCount > 0}
            showWarnings={showWarnings}
            editableFields={permissions?.editable_campaign_fields ?? []}
          />
          <Activity campaignId={campaignId} onApprovalDeleted={() => setIsApproved(false)} />
        </div>
      </div>
    </div>
  );
}
